import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    name: 'Free Trial',
    price: '£0',
    period: '14 days',
    description: 'Try everything free, no card required.',
    features: [
      'All Pro features included',
      'eBay + Vinted cross-listing',
      'AI title & description generator',
      'Photo background removal',
      'Price advisor',
      'Up to 20 listings',
    ],
    cta: 'Start free trial',
    ctaTo: '/signup',
    highlight: false,
    badge: null,
  },
  {
    name: 'Starter',
    price: '£9.99',
    period: 'per month',
    description: 'For casual sellers listing regularly.',
    features: [
      '200 listings per month',
      '5 connected platforms',
      'AI assistant (titles, descriptions, tags)',
      'Basic analytics',
      'Email support',
    ],
    cta: 'Get Starter',
    ctaTo: '/signup?plan=starter',
    highlight: false,
    badge: null,
  },
  {
    name: 'Pro',
    price: '£24.99',
    period: 'per month',
    description: 'For serious sellers who want to scale.',
    features: [
      'Unlimited listings',
      'All 10 platforms',
      'Full AI suite',
      'Auto-delist when item sells',
      'Price advisor',
      'Advanced analytics',
      'Priority support',
    ],
    cta: 'Get Pro',
    ctaTo: '/signup?plan=pro',
    highlight: true,
    badge: 'Most popular',
  },
];

const COMPARISON = [
  { feature: 'Platforms', trial: 'All 10', starter: '5', pro: 'All 10' },
  { feature: 'Listings per month', trial: '20', starter: '200', pro: 'Unlimited' },
  { feature: 'AI title generator', trial: true, starter: true, pro: true },
  { feature: 'AI description generator', trial: true, starter: true, pro: true },
  { feature: 'Price advisor', trial: true, starter: false, pro: true },
  { feature: 'Auto-delist on sale', trial: true, starter: false, pro: true },
  { feature: 'Photo background removal', trial: true, starter: true, pro: true },
  { feature: 'Analytics', trial: 'Basic', starter: 'Basic', pro: 'Advanced' },
  { feature: 'Support', trial: 'Email', starter: 'Email', pro: 'Priority' },
];

function Tick({ value }: { value: boolean | string }) {
  if (typeof value === 'string') {
    return <span className="text-[11.5px] text-white/60">{value}</span>;
  }
  return value
    ? <Check size={14} className="text-secondary-400 mx-auto" />
    : <span className="text-white/20 text-xs">—</span>;
}

export function Pricing() {
  return (
    <div className="min-h-screen bg-bg-base text-white">
      {/* Nav */}
      <nav className="border-b border-white/[0.06] px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary-500 rounded-lg flex items-center justify-center font-bold text-white text-sm">Cr</div>
          <span className="font-semibold tracking-tight">Cros<span className="text-secondary-400">ova</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-[13px] text-white/50 hover:text-white transition-colors">Log in</Link>
          <Link to="/signup" className="btn-primary text-[13px] px-4 py-1.5">Start free trial</Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold tracking-tight mb-3">Simple, honest pricing</h1>
          <p className="text-white/50 text-lg max-w-lg mx-auto">
            Start with a 14-day free trial. No credit card required.
            Cancel anytime.
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`card p-7 flex flex-col relative ${
                plan.highlight ? 'border-primary-500/40 ring-1 ring-primary-500/20' : ''
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-[11px] font-semibold px-3 py-0.5 rounded-full">
                  {plan.badge}
                </div>
              )}

              <div className="mb-5">
                <div className="text-[13px] text-white/40 font-medium mb-1">{plan.name}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-sm text-white/40">{plan.period}</span>
                </div>
                <p className="text-[12px] text-white/40 mt-1.5">{plan.description}</p>
              </div>

              <ul className="space-y-2 mb-7 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[12.5px] text-white/70">
                    <Check size={13} className="text-secondary-400 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                to={plan.ctaTo}
                className={`w-full text-center py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                  plan.highlight
                    ? 'bg-primary-500 hover:bg-primary-400 text-white'
                    : 'bg-white/[0.07] hover:bg-white/[0.12] text-white'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Feature comparison table */}
        <div className="card overflow-hidden mb-16">
          <div className="px-6 py-4 border-b border-white/[0.05]">
            <h2 className="text-sm font-semibold">Full feature comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-white/[0.05]">
                  <th className="text-left px-6 py-3 text-white/40 font-medium">Feature</th>
                  <th className="text-center px-4 py-3 text-white/40 font-medium">Trial</th>
                  <th className="text-center px-4 py-3 text-white/40 font-medium">Starter</th>
                  <th className="text-center px-4 py-3 text-white/40 font-medium text-primary-300">Pro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-white/[0.04] ${i % 2 === 0 ? '' : 'bg-white/[0.01]'}`}>
                    <td className="px-6 py-3 text-white/60">{row.feature}</td>
                    <td className="px-4 py-3 text-center"><Tick value={row.trial} /></td>
                    <td className="px-4 py-3 text-center"><Tick value={row.starter} /></td>
                    <td className="px-4 py-3 text-center"><Tick value={row.pro} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-16">
          <h2 className="text-xl font-semibold mb-6 text-center">Frequently asked questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'Do I need a credit card to start the trial?',
                a: 'No. You can sign up and use Crosova free for 14 days without entering any payment details.',
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes. Cancel from your account settings at any time. You keep access until the end of your billing period.',
              },
              {
                q: 'What platforms are supported?',
                a: 'eBay and Vinted are fully live. Depop, Etsy, Poshmark, Grailed, Mercari, Facebook Marketplace, Shopify, and Whatnot are coming soon.',
              },
              {
                q: 'Is my data safe?',
                a: 'Yes. We never store your marketplace passwords. OAuth connections use secure tokens, and all data is encrypted in transit and at rest.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="card p-5">
                <div className="text-[13px] font-medium mb-1.5">{q}</div>
                <div className="text-[12px] text-white/50">{a}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-white/40 text-sm mb-4">Ready to list once and sell everywhere?</p>
          <Link to="/signup" className="btn-primary px-8 py-3 text-sm">
            Start your free 14-day trial
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] mt-16 py-8 text-center text-[11.5px] text-white/30">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
          <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
          <a href="mailto:admin@crosova.com" className="hover:text-white/60 transition-colors">Contact</a>
        </div>
        <div>© {new Date().getFullYear()} Crosova. All rights reserved.</div>
      </footer>
    </div>
  );
}
